<script setup>
import { onBeforeMount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWorkflows } from '@/services/workflowService.js'
import { getCampaign, getCampaignTemplates, getCampaignTypes } from '@/services/campaignService.js'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'

library.add(faTrash)

const $route = useRoute()
const router = useRouter()
const template = ref([])
const selectedTemplate = ref(null)
const selectedWorkflow = defineModel('selectedWorkflow')
const selectedType = defineModel('selectedType')
const selectedFixed = ref(false)
const isUploading = ref(false)
const isLoading = ref(true)
const templateError = ref({
  enabled: false,
  message: 'Seleziona un template'
})
const networkError = ref({
  enabled: false,
  message: 'Errore nel salvataggio dei dati , riprova più tardi'

})
const workflowError = ref({
  enabled: false,
  message: 'Seleziona un workflow'
})
const titleError = ref({
  enabled: false,
  message: 'Titolo obbligatorio'
})
const dateStartError = ref({
  enabled: false,
  message: 'Data di inizio obbligatoria'
})
const dateEndError = ref({
  enabled: false,
  message: 'La data di fine deve essere maggiore della data odierna'
})
const workflow = ref([])
const types = ref([])
const attributes = ref()
const newAttributeKey = ref('')
const newAttributeValue = ref('')
const title = defineModel('title')
const dateStart = defineModel('dateStart')
const dateEnd = defineModel('dateEnd')
const props = defineProps(['updatePageTitle'])

const campaignId = $route['params'].id
const campaign = ref(null)

onBeforeMount(async () => {
  const campaignTemplates = await getCampaignTemplates()
  template.value = campaignTemplates.data

  workflow.value = await getWorkflows('campaign')

  types.value = await getCampaignTypes()

  await detailCampaign()
  props.updatePageTitle('Modifica campagna - ' + campaign.value.title)
  isLoading.value = false
})

const detailCampaign = async () => {
  try {
    campaign.value = await getCampaign(campaignId)

    selectedTemplate.value = {
      reference: campaign.value.pollTemplateReference,
      revisionNumber: campaign.value.pollTemplateRevisionNumber
    }
    selectedWorkflow.value = campaign.value.workflow
    selectedFixed.value = campaign.value.fixed
    selectedType.value = campaign.value.type
    title.value = campaign.value.title
    dateStart.value = campaign.value.dateStart.split('T')[0]
    dateEnd.value = campaign.value.dateEnd ? campaign.value.dateEnd.split('T')[0] : null

    if (!campaign.value.attributes) {
      campaign.value.attributes = {}
    } else {
      attributes.value = campaign.value.attributes
    }
  } catch (error) {
  }
}

const submitForm = async () => {
  isUploading.value = true
  networkError.value.enabled = false
  templateError.value.enabled = false
  workflowError.value.enabled = false
  titleError.value.enabled = false

  if (!validateCampaign()) {
    return
  }

  if (!attributes.value) {
    attributes.value = {}
  }

  if (!selectedTemplate.value) {
    templateError.value.enabled = true
    return
  }

  if (!selectedWorkflow.value) {
    workflowError.value.enabled = true
    return
  }

  if (!title.value) {
    titleError.value.enabled = true
    return
  }

  const payload = {
    pollTemplateReference: selectedTemplate.value.reference,
    pollTemplateRevisionNumber: selectedTemplate.value.revisionNumber,
    fixed: selectedFixed.value,
    attributes: Object.keys(attributes.value).length ? attributes.value : null,
    workflow: selectedWorkflow.value,
    title: title.value,
    type: selectedType.value,
    dateStart: dateStart.value,
    dateEnd: dateEnd.value ? dateEnd.value : null
  }

  try {
    let url = `/campaign/api/campaign/${campaignId}`
    await client.put(url, payload, 'application/json')
    toast.create('Campagna aggiornata con successo', 'success')
  } catch (error) {
    isUploading.value = false
    toast.create('Errore ottenimento dati campagna', 'error')
  } finally {
    isUploading.value = false
  }

  await router.push({ name: 'admin/Campaign' })
}

const addAttribute = () => {
  if (!attributes.value) {
    attributes.value = {}
  }

  if (newAttributeKey.value && newAttributeValue.value) {
    attributes.value = {
      ...attributes.value,
      [newAttributeKey.value]: newAttributeValue.value
    }
    newAttributeKey.value = ''
    newAttributeValue.value = ''
  }
}

const removeAttribute = (key) => {
  delete attributes.value[key]
}

function showPicker(e) {
  e.target.showPicker()
}

const validateCampaign = () => {
  titleError.value.enabled = false
  dateStartError.value.enabled = false
  dateEndError.value.enabled = false
  if (!title.value || title.value.trim() === '') {
    titleError.value.enabled = true
  }
  if (!dateStart.value || dateStart.value.trim() === '') {
    dateStartError.value.enabled = true
    dateEndError.value.message = 'Data di inizio obbligatoria'
  } else {
    const dmyS = dateStart.value.split('-')
    const dateStartD = new Date(dmyS[0], dmyS[1] - 1, dmyS[2])
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    if (campaign.value.state === 'inactive' && dateStartD.getTime() < now.getTime()) {
      dateEndError.value.enabled = true
      dateEndError.value.message = 'La data di inizio non può essere antecedente a quella odierna'
    }
  }
  if (dateEnd.value) {
    const dmyE = dateEnd.value.split('-')
    const dateEndD = new Date(dmyE[0], dmyE[1] - 1, dmyE[2])
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    if (campaign.value.state !== 'closed' && dateEndD.getTime() <= now.getTime()) {
      dateEndError.value.enabled = true
      dateEndError.value.message = 'La data di fine deve essere dopo quella odierna'
    }

    const dmyS = dateStart.value.split('-')
    const dateStartD = new Date(dmyS[0], dmyS[1] - 1, dmyS[2])
    if (campaign.value.state !== 'closed' && dateEndD.getTime() < dateStartD.getTime()) {
      dateEndError.value.enabled = true
      dateEndError.value.message = 'La data di fine non può essere antecedente a quella di inizio'
    }
  }

  return !(titleError.value.enabled || dateStartError.value.enabled || dateEndError.value.enabled)
}

</script>

<template>
  <div class="container-fluid px-0">
    <div class="card">
      <!--      <div class="card-header bg-primary text-center text-white text-uppercase">-->
      <!--        <h5 class="mb-0">-->
      <!--          Modifica campagna-->
      <!--        </h5>-->
      <!--      </div>-->
      <div class="card-body p-0 m-3" v-if="isLoading">
        <div class="d-flex justify-content-center">
          <div class="spinner-grow" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
      <div class="card-body p-0 m-3" v-else>
        <div v-if="workflowError.enabled" class="alert alert-danger mt-2 mx-0" role="alert">
          {{ workflowError.message }}
        </div>

        <div v-if="templateError.enabled" class="alert alert-danger mt-2 mx-0" role="alert">
          {{ templateError.message }}
        </div>

        <div v-if="titleError.enabled" class="alert alert-danger mt-2 mx-0" role="alert">
          {{ titleError.message }}
        </div>

        <div
          v-if="dateStartError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ dateStartError.message }}
        </div>

        <div
          v-if="dateEndError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ dateEndError.message }}
        </div>

        <div class="mb-3">
          <label for="template">Template *</label>
          <v-select
            :options="template"
            label="title"
            :reduce="temp => ({ reference: temp.reference, revisionNumber: temp.revisionNumber })"
            v-model="selectedTemplate"
            placeholder="Seleziona un template"
            disabled
            readonly
          >
          </v-select>
        </div>
        <div class="mb-3">
          <label for="">Attributi</label>
          <div v-for="(value, key) in attributes" :key="key" class="mb-2">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text rounded-end-0">{{ key }}</span>
              </div>
              <input
                type="text"
                class="form-control"
                v-model="attributes[key]"
                placeholder="Modifica valore"
              />
              <div class="input-group-append">
                <button class="btn btn-danger h-100 rounded-start-0" @click="removeAttribute(key)">
                  <font-awesome-icon icon=" fa-trash" />
                </button>
              </div>
            </div>
          </div>

          <div class="input-group mt-3">
            <input
              type="text"
              class="form-control"
              v-model="newAttributeKey"
              placeholder="Nuovo attributo"
            />
            <input
              type="text"
              class="form-control"
              v-model="newAttributeValue"
              placeholder="Valore"
            />
            <div class="input-group-append">
              <button class="btn btn-secondary h-100 rounded-start-0" @click="addAttribute">
                Aggiungi Attributo
              </button>
            </div>
          </div>
        </div>
        <div class="container-fluid mb-2 px-0">
          <div class="row">
            <div class="col-12 col-lg-6">
              <label for="dateStart">Data inizio campagna *</label>
              <input
                id="dateStart"
                type="date"
                class="form-control"
                v-model="dateStart"
                @click="showPicker"
                placeholder="Data dal"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6">
              <label for="dateEnd">Data fine campagna</label>
              <input
                id="dateEnd"
                type="date"
                class="form-control"
                v-model="dateEnd"
                @click="showPicker"
                placeholder="Data al"
              />
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label for="workflow">Workflow *</label>
          <v-select
            v-model="selectedWorkflow"
            :options="workflow"
            :reduce="perm => perm.key"
            required
            placeholder="Seleziona un workflow"
            disabled
            readonly
          >
          </v-select>
        </div>

        <div class="d-flex flex-wrap">
          <div class="mb-3 col-12 col-lg-6 pe-lg-2 px-0">
            <label for="title">Titolo *</label>
            <input
              type="text"
              class="form-control"
              v-model="title"
              placeholder="Titolo"
              required
            />
          </div>

          <div class="mb-3 col-12 col-lg-6 pe-0">
            <label for="type">Tipologia</label>
            <v-select
              v-model="selectedType"
              :options="types"
              :reduce="perm => perm.key"
              placeholder="Seleziona una Tipologia"
            >
            </v-select>
          </div>
        </div>
        <div class="form-check mb-3">
          <input class="form-check-input" type="checkbox" id="flexCheckDefault" v-model="selectedFixed">
          <label class="form-check-label" for="fixed">
            Fissa
          </label>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <button @click="router.back" class="btn btn-outline-secondary me-auto">
        Annulla
      </button>

      <button type="button" class="btn btn-primary ms-2" @click="submitForm" :disabled="isUploading">
            <span v-if="!isUploading">
              Salva
            </span>
        <span v-else-if="isUploading"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true">
            </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss">
$font-size-base: 1rem;
.fa-trash {
  width: 0.9rem;
}

.container-fluid,
.modal {
  font-family: var(--bs-font-sans-serif), serif;
  font-size: $font-size-base;

  & .table-responsive {
    overflow-y: visible;
  }

  & .vs__search {
    font-size: 1rem !important;
  }

  & .vs__dropdown-toggle, .vs__dropdown-toggle {
    border-color: #dee2e6;
  }
}

.vs__dropdown-toggle {
  font-size: 1rem;
  padding: 0.25rem 0.10rem !important;
}
</style>
